import { Link } from "react-router-dom";
import { Box, Soup, Briefcase, Drumstick, TreePine, ToyBrick, Heart } from "lucide-react";
import type { Article } from "./types";
import { articlesMeta } from "./_meta";
import { SectionHeading } from "@/components/article/SectionHeading";
import { Callout } from "@/components/article/Callout";

const META = articlesMeta.find((m) => m.slug === "jak-przygotowac-sie-do-adopcji-kota")!;

const ProductLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer nofollow"
    className="text-primary font-semibold underline decoration-primary/30 underline-offset-2 hover:decoration-primary transition-colors"
  >
    {children}
  </a>
);

const Body = () => (
  <>
    <p>
      Adopcja kota to duża zmiana - i dla kota, i dla człowieka. Nic dziwnego, że przed adopcją pojawia się sporo
      pytań i stresu. Wbrew pozorom to całkiem dobry znak, bo oznacza, że podchodzisz do tego odpowiedzialnie.
    </p>
    <p>
      W internecie można znaleźć ogromne listy rzeczy „niezbędnych" przed adopcją kota, ale prawda jest taka, że{" "}
      <strong>większość rzeczy da się dokupić później</strong>. Na początku dużo ważniejsze jest spokojne
      przygotowanie mieszkania i świadomość, że kot może potrzebować czasu, żeby poczuć się bezpiecznie.
    </p>
    <p>
      Niektóre koty od pierwszego dnia zwiedzają całe mieszkanie i zachowują się tak, jakby mieszkały tam od
      zawsze. Inne przez kilka dni siedzą pod kanapą i wychodzą dopiero w nocy.{" "}
      <strong>Każdy kot adaptuje się w swoim tempie</strong> i obie reakcje są całkowicie normalne.
    </p>

    <Callout variant="warning" title="Zabezpiecz okna i balkon">
      <p>
        Koty są zwierzętami domowymi i nie powinny samodzielnie wychodzić na dwór. Samochody, trucizny, wypadki
        czy zaginięcia to niestety codzienność. Siatka na balkon i zabezpieczenia okien to kwestia
        bezpieczeństwa, a nie „przesady".
      </p>
    </Callout>

    <p>
      Warto też przygotować kotu spokojny kąt. Nie chodzi o idealnie urządzony „cat room", tylko miejsce, gdzie
      będzie miał kuwetę, miski, legowisko albo kryjówkę i trochę spokoju na początku. Dla kota sama zmiana
      miejsca jest dużym stresem, nawet jeśli trafia do najlepszego domu.
    </p>

    <h2 id="co-kupic">Co warto kupić przed adopcją kota?</h2>
    <p>Najważniejsze, żeby rzeczy były wygodne i praktyczne - zarówno dla kota, jak i dla Ciebie.</p>

    <SectionHeading id="kuweta" icon={Box}>
      Kuweta
    </SectionHeading>

    <Callout variant="warning" title="Najczęstszy błąd? Za mała kuweta.">
      <p>
        Kot, który nie może się w niej swobodnie obrócić, zaczyna jej unikać lub załatwiać się obok. Kuweta
        powinna być na tyle duża, żeby kot mógł się <strong>swobodnie obrócić, stanąć i zakopać żwirek</strong>.
      </p>
    </Callout>

    <p>
      Najlepiej sprawdzają się większe kuwety, do których kot wchodzi od boku, a nie od góry. Te „od góry" często
      wyglądają estetycznie dla człowieka, ale dla kota bywają po prostu niewygodne - szczególnie dla starszych,
      większych albo bardziej stresujących się kotów. Kot powinien mieć możliwość łatwego wejścia, rozejrzenia
      się i szybkiego wyjścia bez poczucia, że siedzi zamknięty w pudełku.
    </p>
    <p>Dobrze sprawdzają się też kuwety z wyższymi bokami, szczególnie jeśli kot lubi mocno kopać w żwirku.</p>
    <p>
      Przykład:{" "}
      <ProductLink href="https://www.zooplus.pl/shop/koty/kuwety_pielegnacja_kota/kuwety_kryte/kuwety_duze/391787?activeVariant=391787.4">
        Savic Nestor Jumbo kuweta XXL
      </ProductLink>
    </p>

    <Callout variant="check" title="Złota zasada: liczba kuwet = liczba kotów + 1">
      <p>
        Czyli przy dwóch kotach najlepiej mieć trzy kuwety. Brzmi absurdalnie, ale serio często robi ogromną
        różnicę - koty lubią mieć wybór i własną przestrzeń.
      </p>
    </Callout>

    <p>
      Ważne jest też samo miejsce kuwety. Nie powinna być schowana w ciasnej szafce bez wentylacji ani stać obok
      misek z jedzeniem. Dużo ludzi wybiera zabudowane szafki, bo wyglądają estetycznie, ale dla kota często są
      po prostu duszne i mało komfortowe.
    </p>

    <SectionHeading id="miski" icon={Soup}>
      Miski
    </SectionHeading>
    <p>
      Najlepiej sprawdzają się <strong>ceramiczne</strong>. Plastikowe miski szybko się rysują, łapią zapach i po
      czasie po prostu są mniej higieniczne.
    </p>
    <p>
      Dobrze, żeby miski były <strong>płaskie i bardziej przypominały talerzyki</strong> niż głębokie miseczki.
      Dużo kotów nie lubi dotykania wibrysami o wysokie brzegi podczas jedzenia - jest to dla nich po prostu
      nieprzyjemne i może powodować dyskomfort.
    </p>
    <p>
      Przykład miseczki:{" "}
      <ProductLink href="https://www.google.com/search?q=Ceramiczny+talerzyk+dla+kota+Trixie">
        Ceramiczny talerzyk dla kota Trixie
      </ProductLink>
    </p>

    <SectionHeading id="transporter" icon={Briefcase}>
      Transporter
    </SectionHeading>
    <p>
      Transporter to jedna z tych rzeczy, których używa się częściej, niż mogłoby się wydawać - wizyty u
      weterynarza, przejazdy czy sytuacje awaryjne. Najwygodniejsze są transportery sztywne, stabilne i
      otwierane od góry.
    </p>
    <p>
      Dzięki temu łatwiej wyjąć kota u weterynarza bez dodatkowego stresu i siłowania się z nim przez małe
      wejście z przodu.
    </p>
    <p>
      Bardzo dobrze sprawdzają się klasyczne plastikowe transportery, np:{" "}
      <ProductLink href="https://www.google.com/search?q=ZOLUX+Transporter+GULLIVER+3+DELUX+Top+Free">
        ZOLUX Transporter GULLIVER 3 DELUX Top Free
      </ProductLink>
    </p>
    <p>
      Warto też zwrócić uwagę, żeby transporter nie był za mały - kot powinien móc się w nim normalnie obrócić
      i położyć.
    </p>
    <Callout variant="tip">
      <p>
        Nie są polecane transportery z dużym przezroczystym frontem ani całkowicie przezroczyste plecaki.
        Dla człowieka wyglądają efektownie, ale wiele kotów bardzo się w nich stresuje, bo są mocno
        wyeksponowane i nie mają poczucia schowania się.
      </p>
    </Callout>

    <SectionHeading id="karma" icon={Drumstick}>
      Karma
    </SectionHeading>
    <p>
      Przy wyborze karmy warto przede wszystkim <strong>zwracać uwagę na skład</strong>. Kot jest bezwzględnym
      mięsożercą, więc dobra karma powinna mieć dużo mięsa i możliwie prosty skład.
    </p>
    <p>
      W karmie <strong>nie powinno być zbóż, cukru ani dużej ilości zbędnych wypełniaczy</strong>. Warto też
      uważać na bardzo ogólne składy typu „produkty pochodzenia zwierzęcego", z których właściwie nie wiadomo,
      z czego się składają.
    </p>
    <p>
      Najlepiej stawiać <strong>głównie na mokrą karmę</strong>. Koty naturalnie piją mało wody, dlatego
      nawodnienie z jedzenia jest dla nich bardzo ważne. Sucha karma jest mocno przetworzona i ma bardzo mało
      wilgoci, przez co przy dłuższym czasie może zwiększać ryzyko problemów z nerkami albo układem moczowym -
      szczególnie u kotów, które mało piją.
    </p>
    <p>
      Dobrym przykładem karm z prostym składem i wysoką zawartością mięsa są karmy{" "}
      <ProductLink href="https://www.google.com/search?q=John+Dog+for+Cats">John Dog for Cats</ProductLink>. Nie
      mają zbóż ani dużej ilości zbędnych dodatków, a jednocześnie są stosunkowo łatwo dostępne i dobrze
      tolerowane przez wiele kotów.
    </p>
    <Callout variant="info" title="A może BARF?">
      <p>
        Jeśli ktoś chce wejść trochę bardziej w temat kociego żywienia, najlepszym rozwiązaniem zwykle jest BARF
        - dieta oparta na surowym mięsie i odpowiednio dobranych suplementach, najbardziej zbliżona do
        naturalnego sposobu żywienia kotów. Wymaga jednak trochę wiedzy i pilnowania proporcji, dlatego lepiej
        potraktować ją jako kierunek na później, a nie pierwszy krok zaraz po adopcji. Warto też skonsultować
        się z kocim dietetykiem - dietę można ułożyć indywidualnie pod wyniki badań i potrzeby konkretnego kota.
      </p>
    </Callout>
    <Callout variant="tip" title="Nie zmieniaj wszystkiego naraz">
      <p>
        Jeśli kot wcześniej jadł konkretną karmę w fundacji albo domu tymczasowym, zbyt szybka zmiana często
        kończy się problemami żołądkowymi. Lepiej przechodzić na nową karmę stopniowo.
      </p>
    </Callout>

    <SectionHeading id="drapak" icon={TreePine}>
      Drapak
    </SectionHeading>
    <p>Najważniejsze w drapaku wcale nie jest to, żeby miał mnóstwo budek, półek i wiszących zabawek.</p>
    <p>Dużo ważniejsze jest to, żeby był:</p>
    <ul>
      <li>stabilny,</li>
      <li>odpowiednio wysoki,</li>
      <li>miał wygodne miejsca do drapania.</li>
    </ul>
    <p>
      Dobrze, żeby kot mógł się podczas drapania swobodnie wyciągnąć, dlatego bardzo małe drapaki często po
      prostu się nie sprawdzają.
    </p>
    <p>
      Jeśli chodzi o naprawdę porządne drapaki „na lata", bardzo dużo osób poleca drapaki{" "}
      <ProductLink href="https://www.google.com/search?q=drapaki+RUFI">RUFI</ProductLink>. Są droższe niż
      większość popularnych drapaków ze sklepów zoologicznych, ale jakościowo robią ogromną różnicę i dobrze
      sprawdzają się też przy większych kotach.
    </p>
    <p>
      Przykładowy drapak:{" "}
      <ProductLink href="https://www.google.com/search?q=Drapak+RUFI+D-5">
        Drapak RUFI D-5 dla dużych kotów
      </ProductLink>
    </p>

    <SectionHeading id="zabawki" icon={ToyBrick}>
      Zabawki
    </SectionHeading>
    <p>
      Koty bardzo często najbardziej interesują najprostsze rzeczy 😅 Najlepsze zabawki to zwykle wędki,
      sprężynki oraz kartony i papierowe torby.
    </p>
    <p>Rekomendacja od moich kotów:</p>
    <ul>
      <li>
        <ProductLink href="https://www.google.com/search?q=W%C4%99dka+Cat+Dancer">Wędka Cat Dancer</ProductLink>
      </li>
      <li>
        <ProductLink href="https://www.google.com/search?q=zestaw+spirali+dla+kota">
          Zestaw spirali dla kota
        </ProductLink>
      </li>
    </ul>
    <Callout variant="tip" title="Smaczki - mały, ale ważny dodatek">
      <p>
        Warto mieć je już na początek - bardzo pomagają budować pozytywne skojarzenia i trochę oswoić kota
        w pierwszych dniach. Fajnie sprawdzają się np.{" "}
        <ProductLink href="https://www.google.com/search?q=Cosma+Snackies">Cosma Snackies</ProductLink>, bo
        mają bardzo prosty skład - to czyste mięso bez zbędnych dodatków.
      </p>
    </Callout>

    <SectionHeading id="cierpliwosc" icon={Heart}>
      Na koniec - cierpliwość
    </SectionHeading>
    <p>
      Dużo osób przed adopcją martwi się też, czy „dadzą radę", szczególnie jeśli wcześniej nie miały kota.
      Prawda jest taka, że <strong>większość opiekunów też uczy się wielu rzeczy dopiero w praktyce</strong>.
      Nie trzeba być perfekcyjnym. Dużo ważniejsze są cierpliwość, odpowiedzialność i gotowość na to, że
      początki mogą wyglądać różnie.
    </p>
    <p>
      I chyba najważniejsze - <strong>warto dać sobie i kotu trochę czasu</strong>. Adaptacja rzadko wygląda
      tak, że po dwóch godzinach kot śpi przytulony pod kocem. Czasem relacja buduje się powoli. Ale właśnie
      wtedy często wychodzi z tego coś naprawdę fajnego.
    </p>
    <p>
      Od siebie polecę jeszcze jedną rzecz - jeśli chcesz naprawdę zrozumieć, dlaczego kot zachowuje się tak,
      a nie inaczej, sięgnij po książkę{" "}
      <ProductLink href="https://www.google.com/search?q=Kocie+Mojo+Jackson+Galaxy+Mikel+Delgado">
        „Kocie Mojo, czyli jak być opiekunem szczęśliwego kota"
      </ProductLink>{" "}
      (Jackson Galaxy, Mikel Delgado). To jedna z tych pozycji, po których wiele rzeczy w głowie się układa - i
      naprawdę zmienia podejście do życia z kotem.
    </p>

    <div className="not-prose my-8 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 p-5 sm:p-6 text-center">
      <p className="text-base sm:text-lg font-semibold text-foreground mb-3">
        Szukasz schroniska albo fundacji w swojej okolicy?
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-5 py-2.5 rounded-full text-sm hover:bg-primary/90 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
      >
        Otwórz mapę schronisk
      </Link>
    </div>
  </>
);

const article: Article = {
  slug: META.slug,
  title: META.title,
  description: META.description,
  publishedAt: META.publishedAt,
  readingMinutes: 9,
  category: META.category,
  excerpt: META.excerpt,
  Body,
};

export default article;
